import {
  ChartSort,
  DEFAULT_SORT,
  DEFAULT_SORT_BREAKDOWN_BOX,
  DEFAULT_SORT_SENTIMENT_POLARITY,
} from '../components/widget-chart/widget-chart.component';

export class ChartSortHelper {
  constructor(
    private seriesData,
    private categoriesData,
    private widget,
    private timeSeries,
    private isCustomDate,
    private isCustomNumeric
  ) {}

  static getCurrentSort(widget): ChartSort {
    let currentSort = widget.payload.sort;
    if (!currentSort) {
      currentSort = ChartSortHelper.getDefaultSort(widget);
    }

    return currentSort;
  }

  static getDefaultSort(widget): ChartSort {
    switch (widget.payload.type) {
      case 'sentimentPolarity':
        return DEFAULT_SORT_SENTIMENT_POLARITY;
      case 'breakdown':
        if (widget.payload.breakdownOptions.scoring === 'box') {
          return DEFAULT_SORT_BREAKDOWN_BOX;
        }
      // intentional!
      // eslint-disable-next-line no-fallthrough
      default:
        return DEFAULT_SORT;
    }
  }

  sortData(): any {
    if (!this.seriesData.some((series) => series.data.length)) {
      return;
    }

    const sort = ChartSortHelper.getCurrentSort(this.widget);
    const sgn = sort.order === 'asc' ? 1 : -1;

    if (sort.field === 'custom') {
      return this.customSortData();
    }

    const valueExtractor = this.seriesValueExtractorFn(
      this.seriesData.find((series) => series.data.length).data,
      this.widget.payload.aggregator
    );

    const categoryComparator = this.categoryComparatorFn(this.isCustomDate, this.isCustomNumeric);

    if (this.timeSeries) {
      return this.sortTimeData(sort, sgn, valueExtractor, categoryComparator, this.seriesData);
    }

    switch (this.widget.payload.type) {
      case 'sentimentPolarity':
        return this.sortSentimentData(
          sort,
          sgn,
          valueExtractor,
          categoryComparator,
          this.seriesData,
          this.categoriesData,
          this.widget.payload.aggregator
        );
      case 'breakdown':
        if (this.widget.payload.breakdownOptions.scoring === 'box') {
          return this.sortBreakdownBox(
            sort,
            sgn,
            valueExtractor,
            categoryComparator,
            this.seriesData
          );
        }
      // intentional!
      // eslint-disable-next-line no-fallthrough
      default:
        return this.sortVolumetricData(
          sort,
          sgn,
          valueExtractor,
          categoryComparator,
          this.seriesData
        );
    }
  }

  private seriesValueExtractorFn: (series, aggregator) => (x) => number = (series, aggregator) => {
    if (!series.length) {
      return () => 0;
    }

    if (typeof series[0] === 'object') {
      if (aggregator === 'percentage') {
        return (item) => +(item.percentage ?? item.y);
      }
      return (item) => +item.y;
    } else if (typeof series[0] === 'number') {
      return (item) => +item;
    }
  };

  private categoryComparatorFn: (isCustomDate, isCustomNumeric) => (a, b) => number = (
    isCustomDate,
    isCustomNumeric
  ) => {
    if (isCustomDate) {
      return (a, b) => a.localeCompare(b);
    }
    if (isCustomNumeric) {
      return (a, b) => a - b;
    }
    return (a, b) => a.localeCompare(b);
  };

  private sortTimeData(widgetSort, sgn, valueExtractor, categoryComparator, seriesData) {
    seriesData.sort((s1, s2) => {
      if (widgetSort.field === 'value') {
        const s1Value = s1.data.reduce((prev, curr) => prev + valueExtractor(curr), 0);
        const s2Value = s2.data.reduce((prev, curr) => prev + valueExtractor(curr), 0);

        return sgn * (s1Value - s2Value);
      } else {
        return (
          sgn * categoryComparator(s1.custom?.seriesId ?? s1.name, s2.custom?.seriesId ?? s2.name)
        );
      }
    });
  }

  private sortSentimentData(
    widgetSort,
    sgn,
    valueExtractor,
    categoryComparator,
    seriesData,
    categoriesData,
    aggregator
  ) {
    const firstPopulatedSerie = seriesData.find((s) => s.data.length);
    if (!firstPopulatedSerie) {
      return;
    }

    // taxonomy should be compared by point display
    const pointDisplays: string[] = firstPopulatedSerie.data.map((d) => d.pointDisplay);

    interface CumulatedData {
      idx: number;
      y: number;
      category: string;
      pointDisplay: string;
      documentCount: number;
    }

    const getFieldToBeCompared = (d: CumulatedData): string => {
      if (this.widget.payload.analyticItem === 'taxonomy') {
        return d.pointDisplay;
      }

      return d.category;
    };

    const cumulatedData: CumulatedData[] = [];

    // percent aggregation is currently done by Highcharts
    // so we don't have that before the first render
    const seriesWithPercentage =
      aggregator === 'total' || typeof firstPopulatedSerie.data[0] === 'object' ? seriesData : [];
    if (!seriesWithPercentage.length) {
      for (const series of seriesData) {
        seriesWithPercentage.push({ name: series.name, data: [] });
      }

      for (let i = 0; i < seriesData[0].data.length; i++) {
        const total = seriesData.reduce((prev, curr) => prev + curr.data[i], 0);
        for (let j = 0; j < seriesData.length; j++) {
          seriesWithPercentage[j].data[i] = Math.round((seriesData[j].data[i] * 100) / total);
        }
      }
    }

    const sortBaseSeriesName = widgetSort.sentiment ?? seriesWithPercentage[0].name;

    seriesWithPercentage
      .filter((s) => s.name.toLocaleLowerCase().includes(sortBaseSeriesName))
      .reduce((prev, curr) => {
        curr.data.forEach((v, i) => (prev[i] += valueExtractor(v)));
        return prev;
      }, Array(firstPopulatedSerie.data.length).fill(0))
      .forEach((d, i) => {
        cumulatedData.push({
          idx: i,
          y: d,
          category: categoriesData[i],
          pointDisplay: pointDisplays[i],
          documentCount: d,
        });
      });

    cumulatedData.sort((a, b) => {
      const categoryCompare =
        sgn * categoryComparator(getFieldToBeCompared(a), getFieldToBeCompared(b));

      if (widgetSort.field === 'value') {
        const valueCompare = sgn * (a.y - b.y);
        if (valueCompare) {
          return valueCompare;
        }
      }
      return categoryCompare;
    });

    const newSeries = [];
    seriesData.forEach((serie) => {
      const newSeriesData = [];
      cumulatedData.forEach((cd, idx) => {
        if (serie.data[cd.idx] !== undefined) {
          const data: any = {
            id: serie.data[cd.idx].id,
            pointId: serie.data[cd.idx].pointId,
            pointDisplay: serie.data[cd.idx].pointDisplay,
            y: serie.data[cd.idx].y ?? serie.data[cd.idx],
            selected: serie.data[cd.idx].selected ?? false,
            documentCount: serie.data[cd.idx].y ?? serie.data[cd.idx],
          };
          if (serie.data[cd.idx].hasOwnProperty('x')) {
            data.x = idx;
          }
          newSeriesData.push(data);
        }
      });
      newSeries.push(newSeriesData);
    });

    return { series: newSeries, categories: cumulatedData.map((cd) => cd.category) };
  }

  private sortVolumetricData(widgetSort, sgn, valueExtractor, categoryComparator, seriesData) {
    const categoryExtractor = (point) => point.pointDisplay ?? point.pointId ?? point.category;

    const nonEmptySeriesData = seriesData.find((series) => series.data.length);

    nonEmptySeriesData.data.sort((a, b) => {
      const categoryA = categoryExtractor(a);
      const categoryB = categoryExtractor(b);
      const categoryCompare = sgn * categoryComparator(categoryA, categoryB);

      if (widgetSort.field === 'value') {
        const valueA = valueExtractor(a);
        const valueB = valueExtractor(b);
        if (isNaN(valueA)) {
          if (isNaN(valueB)) {
            return 0;
          }
          return -sgn;
        }

        if (isNaN(valueB)) {
          return sgn;
        }

        const valueCompare = sgn * (valueA - valueB);
        if (valueCompare) {
          return valueCompare;
        }
      }
      return categoryCompare;
    });

    const newCategories = [];

    nonEmptySeriesData.data.forEach((e, idx) => {
      e.x = idx;
      newCategories.push(e.pointId);
    });

    for (let i = 1; i < seriesData.length; i++) {
      seriesData[i].data.sort(
        (a, b) => newCategories.indexOf(a.pointId) - newCategories.indexOf(b.pointId)
      );

      seriesData[i].data.forEach((e, idx) => (e.x = idx));
    }

    return { categories: newCategories };
  }

  private sortBreakdownBox(widgetSort, sgn, valueExtractor, categoryComparator, seriesData) {
    const sortBaseSeriesName = widgetSort.sentiment ?? seriesData[0].name;

    if (!seriesData.some((sd) => sd.name === sortBaseSeriesName) && widgetSort.field === 'value') {
      return;
    }

    const newSeries = [];

    for (const series of seriesData) {
      newSeries.push({
        name: series.name,
        data: series.data.map((d) => {
          const data: any = {
            id: d.id,
            pointId: d.pointId,
            y: d.y,
          };
          if (d.hasOwnProperty('x')) {
            data.x = d.x;
          }
          return data;
        }),
      });
    }

    const sortBaseSeries = newSeries.find((s) => s.name === sortBaseSeriesName) ?? newSeries[0];

    sortBaseSeries.data.sort((a, b) => {
      if (widgetSort.field === 'value') {
        return sgn * (valueExtractor(a) - valueExtractor(b));
      } else {
        const categoryA = a.pointId ?? a.category;
        const categoryB = b.pointId ?? b.category;

        return sgn * categoryComparator(categoryA, categoryB);
      }
    });

    const newCategories = sortBaseSeries.data.map((sbs) => sbs.pointId);

    newSeries
      .filter((s) => !s.name.toLocaleLowerCase().includes(sortBaseSeries.name))
      .forEach((s) =>
        s.data
          .sort((a, b) => newCategories.indexOf(a.pointId) - newCategories.indexOf(b.pointId))
          .forEach((d, idx) => {
            if (d.hasOwnProperty('x')) {
              d.x = idx;
            }
          })
      );

    return { series: newSeries.map((series) => series.data), categories: newCategories };
  }

  private customSortData() {
    const order = this.widget.payload.sort.items;

    if (this.timeSeries) {
      return this.customSortTimeData(order, this.seriesData);
    }

    switch (this.widget.payload.type) {
      case 'sentimentPolarity':
        return this.customSortSentimentData(order, this.seriesData, this.categoriesData);
      case 'breakdown':
        if (this.widget.payload.breakdownOptions.scoring === 'box') {
          return this.customSortBreakdownBox(order, this.seriesData, this.categoriesData);
        }
      // intentional!
      // eslint-disable-next-line no-fallthrough
      default:
        return this.customSortVolumetricData(order, this.seriesData);
    }
  }

  private customSortTimeData(order, seriesData) {
    const comparatorFn = this.customComparatorFn(
      order,
      (item) => item.custom?.seriesId ?? item.name
    );

    seriesData.sort(comparatorFn);
  }

  private customSortSentimentData(order, seriesData, categoriesData) {
    const comparatorFn = this.customComparatorFn(order, (item) => item.category);
    const indexedCategories = categoriesData.map((data, i) => ({ idx: i, category: data }));

    indexedCategories.sort(comparatorFn);

    const newSeries = [];
    seriesData.forEach((serie) => {
      const newSeriesData = [];
      indexedCategories.forEach((cd, idx) => {
        if (serie.data[cd.idx] !== undefined) {
          const data: any = {
            id: serie.data[cd.idx].id,
            pointId: serie.data[cd.idx].pointId,
            pointDisplay: serie.data[cd.idx].pointDisplay,
            y: serie.data[cd.idx].y ?? serie.data[cd.idx],
            selected: serie.data[cd.idx].selected ?? false,
          };
          if (serie.data[cd.idx].hasOwnProperty('x')) {
            data.x = idx;
          }
          newSeriesData.push(data);
        }
      });
      newSeries.push(newSeriesData);
    });

    return { series: newSeries, categories: indexedCategories.map((cd) => cd.category) };
  }

  private customSortVolumetricData(order, seriesData) {
    const categoryExtractor = (point) => point.pointId ?? point.category;
    const comparatorFn = this.customComparatorFn(order, categoryExtractor);

    seriesData[0].data.sort(comparatorFn);

    const newCategories = [];

    seriesData[0].data.forEach((e, idx) => {
      e.x = idx;
      newCategories.push(e.pointId);
    });

    for (let i = 1; i < seriesData.length; i++) {
      seriesData[i].data.sort(comparatorFn);
      seriesData[i].data.forEach((e, idx) => (e.x = idx));
    }

    return { categories: newCategories };
  }

  private customSortBreakdownBox(order, seriesData, categoriesData) {
    const comparatorFn = this.customComparatorFn(order, (item) => item.pointId);
    const indexedCategories = categoriesData
      .map((data, i) => ({ idx: i, pointId: data }))
      .sort(comparatorFn);

    const newSeries = [];
    for (const series of seriesData) {
      const newSeriesData = series.data
        .map((d) => {
          const data: any = {
            id: d.id,
            pointId: d.pointId,
            y: d.y,
          };
          if (d.hasOwnProperty('x')) {
            data.x = d.x;
          }
          return data;
        })
        .sort(comparatorFn);

      newSeriesData.forEach((d, idx) => {
        if (d.hasOwnProperty('x')) {
          d.x = idx;
        }
      });

      newSeries.push(newSeriesData);
    }

    return { series: newSeries, categories: indexedCategories.map((cd) => cd.pointId) };
  }

  private customComparatorFn(order, categoryExtractor): (a, b) => number {
    return (a, b) => {
      const indexA = order.indexOf(categoryExtractor(a));
      const indexB = order.indexOf(categoryExtractor(b));

      if (indexA === -1) {
        if (indexB === -1) {
          return 0;
        }
        return 1;
      } else if (indexB === -1) {
        return -1;
      }

      return indexA - indexB;
    };
  }
}
